import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setStoredToken, getStoredToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = not auth, object = user
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        // No stored token → skip the request entirely (avoids noisy 401s in console)
        if (!getStoredToken()) {
            setUser((prev) => (prev && typeof prev === "object" ? prev : false));
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            // Don't clobber a logged-in user that was set in parallel by
            // applyAuthResponse (e.g. magic-login race condition).
            setUser((prev) => (prev && typeof prev === "object" ? prev : false));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const _persistAuth = (data) => {
        if (data?.access_token) {
            setStoredToken(data.access_token);
        }
        // Strip the token from the user object stored in state (cosmetic)
        const { access_token, ...userOnly } = data || {};
        setUser(userOnly);
    };

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            _persistAuth(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            _persistAuth(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        setStoredToken("");
        setUser(false);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh: fetchMe, setUser, applyAuthResponse: _persistAuth }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
